import {
  AbsoluteCenter,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  ModalCloseButton,
  PinInput,
  Text,
} from '@chakra-ui/react';
import { useState } from 'react';

import DeclineApprovalImg from '../../../../../assets/images/decline-approval-img.svg';
import {
  AnimatedLogo,
  CustomModalActionPrimaryButton,
  CustomModalFooter,
  CustomPinInputField,
  DangerButton,
  PrimaryButton,
  SecondaryButton,
  StatusBox,
} from '../../../../../components/custom';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import {
  bulkDeclineTransactionAsync,
  resetTransactionBulkDecline,
} from '../../../../../store/slices/transactionMandate/bulkDeclineTransaction';
import CreateTransactionPin from '../../../../TransactionPin/CreateTransactionPin';

interface BulkDeclineTransactionsProps {
  selectedTransactions: (string | number)[];
  handleResetSelectedTransactions: () => void;
}

type TDeclineScreens = 'selection' | 'reason' | 'pin';

export default function BulkDeclineTransactions({
  selectedTransactions,
  handleResetSelectedTransactions,
}: BulkDeclineTransactionsProps) {
  const [declineScreen, setDeclineScreen] =
    useState<TDeclineScreens>('selection');

  const handleDecline = () => {
    setDeclineScreen('reason');
  };

  const [reason, setReason] = useState('');

  const [pin, setPin] = useState('');

  const dispatch = useAppDispatch();

  const { status, value } = useAppSelector(
    (state) => state.bulkDeclineTransaction
  );

  const handleSubmitDeclineTransaction = () => {
    dispatch(
      bulkDeclineTransactionAsync({
        reason,
        pin,
        approvalIds: selectedTransactions.map((transaction) =>
          transaction.toString()
        ),
      })
    );
  };

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  if (!userHasPin) {
    return <CreateTransactionPin setUserHasPin={setUserHasPin} />;
  }

  if (declineScreen === 'reason') {
    return (
      <Box>
        <Box pt={10} pb={4}>
          <Text
            color="#1a1c1f"
            fontSize="1.5rem"
            lineHeight="133.33%"
            mb={3}
            textAlign="center"
          >
            Write reason
          </Text>
          <Text
            fontSize="16px"
            color="gray.200"
            lineHeight="143.75%"
            textAlign="center"
            mb={3}
          >
            Please provide the reasons for rejecting these transactions.
          </Text>
        </Box>

        <FormControl id="reason" mb="115px">
          <FormLabel>Reason For Rejection</FormLabel>
          <Input
            placeholder="State your reason here"
            maxLength={50}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormControl>

        <CustomModalFooter>
          <CustomModalActionPrimaryButton
            isDisabled={!reason}
            onClick={() => setDeclineScreen('pin')}
          >
            Continue
          </CustomModalActionPrimaryButton>
        </CustomModalFooter>
      </Box>
    );
  }

  if (declineScreen === 'pin') {
    if (status === 'loading') {
      return (
        <Box minH="330px">
          <AbsoluteCenter>
            <AnimatedLogo />
          </AbsoluteCenter>
        </Box>
      );
    }

    if (status === 'success') {
      handleResetSelectedTransactions();

      return (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox
                variant="success"
                title="Transactions Declined Successfully."
              >
                {value?.message ??
                  'The transactions have been successfully declined.'}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomModalFooter>
            <PrimaryButton
              as={ModalCloseButton}
              w="full"
              top="initial"
              right="initial"
            >
              Dismiss
            </PrimaryButton>
          </CustomModalFooter>
        </>
      );
    }

    if (status === 'failed') {
      return (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox variant="error" title="Failed to decline transaction.">
                {value?.message ??
                  'An error occurred while declining the transactions. Please try again.'}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomModalFooter>
            <PrimaryButton
              onClick={() => dispatch(resetTransactionBulkDecline())}
              w="full"
            >
              Retry
            </PrimaryButton>
          </CustomModalFooter>
        </>
      );
    }

    return (
      <Box>
        <Box pt={10} pb={4}>
          <Text
            color="#1a1c1f"
            fontSize="1.5rem"
            lineHeight="133.33%"
            mb={3}
            textAlign="center"
          >
            PIN
          </Text>
          <Text
            fontSize="16px"
            color="gray.200"
            lineHeight="143.75%"
            textAlign="center"
            mb={3}
          >
            Enter your 4-digit PIN to reject this transaction
          </Text>
        </Box>

        <FormControl id="pin" mb="114px">
          <FormLabel fontSize=".875rem">Enter PIN</FormLabel>
          <HStack>
            <PinInput mask value={pin} onChange={(value) => setPin(value)}>
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
            </PinInput>
          </HStack>
        </FormControl>

        <CustomModalFooter>
          <CustomModalActionPrimaryButton
            isDisabled={!pin}
            onClick={handleSubmitDeclineTransaction}
          >
            Reject Transactions
          </CustomModalActionPrimaryButton>
        </CustomModalFooter>
      </Box>
    );
  }

  return (
    <Box>
      <Box pt={10} pb={4}>
        <Image
          src={DeclineApprovalImg}
          alt=""
          loading="lazy"
          boxSize={200}
          mx="auto"
          mb={4}
        />
        <Text fontSize="24px" lineHeight="133%" textAlign="center" mb={3}>
          Decline Transaction
        </Text>
        <Text color="gray.200" textAlign="center" letterSpacing="-0.12px">
          Are you sure you want to decline{' '}
          {selectedTransactions.length === 1 ? 'this' : 'these'}{' '}
          {selectedTransactions.length}{' '}
          {selectedTransactions.length === 1 ? 'transaction' : 'transactions'}?
          This action is irreversible.
        </Text>
      </Box>
      <CustomModalFooter>
        <HStack spacing={4}>
          <DangerButton w="full" onClick={handleDecline}>
            Yes, Decline
          </DangerButton>
          <SecondaryButton
            w="full"
            as={ModalCloseButton}
            top="initial" // reset ModalCloseButton styles
            right="initial"
            flexShrink="initial"
          >
            Cancel, Keep
          </SecondaryButton>
        </HStack>
      </CustomModalFooter>
    </Box>
  );
}
